import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';

// library components
import { Formik, Form } from 'formik';
import { Button, Grid, Box, InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DoneIcon from '@material-ui/icons/Done';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { useConfirm } from 'material-ui-confirm';

import { useEffect } from 'react';

//validation library
import * as Yup from 'yup';
import moment from 'moment';
import { some, values, trimEnd, map } from 'lodash';

import {
  CERT_PROJECT_BOARDS_PROVIDER_SELECT_OPTIONS,
  CERT_PROJECT_BOARDS_PROVIDER,
  MEETING_PROVIDERS,
  MEETING_PROVIDER_SELECT_OPTIONS,
  PROJECT_CALENDAR_EVENT_RECURRENCE_SELECT_OPTIONS,
  PROJECT_LOCATION_TYPES,
  PROJECT_SERVICE_SELECT_OPTIONS,
  PROJECT_SERVICES,
} from 'constants/staffing';

import { useStaffingSelector } from 'redux/selectors/staffing/staffingSelectors';
import {
  selectProject,
  selectProjectMethodologies,
  selectDealsDropdown,
} from 'redux/selectors/staffing/getProject';
import {
  createProject,
  createProjectMethodology,
  openManageProjectMethodologiesDialog,
  updateProject,
  setDealsFilterPayload,
} from 'redux/actions/staffingActions';
import { selectDeal } from 'redux/selectors/staffing/getDeals';
import { enqueueErrorSnackbar } from 'redux/actions/notifierActions';
import { getApiErrorMessage, getApiFieldErrors } from 'lib/getApiFieldErrors';
import StatusErrorDialog from 'components/Dialogs/StatusErrorDialog';
import { useStatusErrorDialog } from 'hooks/useStatusErrorDialog';

import TextInput from 'components/CustomFormComponents/TextInput';
import MaterialDate from 'components/CustomFormComponents/MaterialDate';
import MaterialTime from 'components/CustomFormComponents/MaterialTime';
import CustomCheckbox from 'components/CustomFormComponents/CustomCheckbox';
import TextArea from 'components/CustomFormComponents/TextArea';
import ButtonSpinner from 'components/ButtonSpinner';
import CustomSelect from 'components/CustomFormComponents/CustomSelect';
import Spinner from 'components/Spinner';

import { PROJECT_LOCATION_TYPES_SELECT_OPTIONS, PROJECT_STATUS_STATES } from 'constants/staffing';
import { formatPrice } from 'utils/formatPrice';
import CustomSearchableSelect from 'components/CustomFormComponents/CustomSearchableSelect';
import { selectConfigSelectOptions } from 'redux/selectors/staffing/getConfig';
import { isDealInitializing, DEAL_INITIALIZING_ADD_PROJECT_MESSAGE } from 'utils/staffing/status';

/**
 * Formik field names that have a rendered input on this form. API validation errors for these
 * keys are pinned with setErrors. Anything else (e.g. `jobs`) has no input to highlight, so those
 * messages go to StatusErrorDialog instead of a misleading "See errors above."
 */
const PROJECT_FORM_FIELD_NAMES = new Set([
  'eventName',
  'dealId',
  'contactName',
  'projectMethodologyName',
  'projectMethodologyId',
  'contactEmail',
  'serviceType',
  'eventTopic',
  'eventType',
  'eventRegion',
  'language',
  'locationType',
  'startDateLocal',
  'endDateLocal',
  'startTimeLocal',
  'endTimeLocal',
  'timezone',
  'meetingProvider',
  'zoomUserEmail',
  'meetingLink',
  'startDate',
  'endDate',
  'startTime',
  'endTime',
  'venueId',
  'isFree',
  'contractValue',
  'eventSubtitle',
  'eventFeaturedContent',
  'notes',
  'certProjectBoardsProvider',
  'calendarEventRecurrenceType',
  'skippedMeetingOccurrences',
]);

/**
 * Event types that always create free Control Room projects.
 * Keep in sync with FREE_EVENT_TYPES in control-room-service
 * `functions/src/lib/projects/validation.ts`.
 */
const FREE_EVENT_TYPES = [
  'ama',
  'meetup',
  'huddle',
  'hub-orientation',
  'mini-workshop',
  'office-hours',
  'volunteer-orientation',
  'fireside-chat',
  'panel',
  'webinar',
];

/**
 * Returns whether the given event type slug is always free.
 *
 * @param {string} eventType Project event type slug (e.g. `meetup`, `webinar`).
 * @returns {boolean} True when this event type forces `isFree`.
 */
const isForcedFreeEventType = eventType => FREE_EVENT_TYPES.includes(eventType);

/**
 * Returns whether the form values force a free project (Skill Bridge or
 * always-free event type). Does not include a manually checked Free box.
 *
 * @param {{ serviceType?: string, eventType?: string }} values Formik values.
 * @returns {boolean} True when Free must be checked and disabled.
 */
const isForcedFree = values =>
  values.serviceType === PROJECT_SERVICES.SkillBridge || isForcedFreeEventType(values.eventType);

/**
 * Syncs the Free checkbox when Service Type or Event Type changes.
 * Skill Bridge and always-free event types check Free; leaving both clears it
 * so Amount can reappear. Keeps Formik `isFree` accurate for submit without
 * re-deriving on submit.
 *
 * @param {string} serviceType Selected service type.
 * @param {string} eventType Selected event type slug.
 * @param {(field: string, value: boolean) => void} setFieldValue Formik setFieldValue.
 */
const syncIsFreeForServiceAndEventType = (serviceType, eventType, setFieldValue) => {
  if (serviceType === PROJECT_SERVICES.SkillBridge || isForcedFreeEventType(eventType)) {
    setFieldValue('isFree', true);
  } else {
    setFieldValue('isFree', false);
  }
};

const useStyles = makeStyles(theme => ({
  dialogActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  saveButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '200px', // Adjust this width as needed
  },

  staticInfoViewLabel: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  staticInfoView: {
    minHeight: 32,
    border: '1px dashed #C9C9C9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 5,
  },
  staticInfoViewTitle: {
    lineHeight: '30px',
    fontSize: 14,
    fontWeight: '400',
    color: '#5E5E5E',
  },
  addMethodologyButton: {
    height: 30,
    width: 30,
    background: theme.palette.primary.main,
    color: '#fff',
    fontSize: 14,
    marginLeft: 0,
    position: 'relative',
    top: 26,
    '&:hover': {
      background: theme.palette.primary.main,
      opacity: 0.9,
    },
    '&:disabled': {
      background: '#9E9E9E',
      color: '#fff',
    },
  },
  addMethodologyButtonGrey: {
    height: 30,
    width: 30,
    background: '#9E9E9E',
    color: '#fff',
    fontSize: 14,
    marginLeft: 0,
    position: 'relative',
    top: 26,
    '&:hover': {
      background: '#9E9E9E',
      opacity: 0.9,
      color: '#fff',
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 6,
  },
  cancelButton: {
    width: 105,
    height: 40,
    marginRight: 'auto',
    background: '#9E9E9E',
    color: '#fff',
    fontSize: 14,
    '&:hover': {
      background: '#9E9E9E',
      opacity: 0.9,
    },
  },
  saveButton: {
    width: 105,
    height: 40,
    background: theme.palette.primary.main,
    color: '#fff',
    fontSize: 14,
    '&:hover': {
      background: theme.palette.primary.main,
      opacity: 0.9,
    },
    '&:disabled': {
      background: '#9E9E9E',
      color: '#fff',
    },
  },

  inputLabel: {},
  selectContainer: {
    marginBottom: 0,
  },
  methodologyLabelRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  // Text-style pencil next to the Methodology label — not a filled IconButton beside +.
  manageMethodologiesLink: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 6,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    lineHeight: 1,
    '&:hover': {
      opacity: 0.75,
    },
    '& svg': {
      fontSize: 16,
    },
  },

  checkboxLabel: {
    lineHeight: '30px',
    fontSize: 12,
    fontWeight: '300',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  errorMessage: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    textAlign: 'right',
    width: '100%',
  },
}));

const isDurationLessThanOrEqual24Hours = (startDate, startTime, endDate, endTime) => {
  if (!startDate || !startTime || !endDate || !endTime) return false;

  const start = moment(`${startDate} ${startTime}`);
  const end = moment(`${endDate} ${endTime}`);
  const duration = moment.duration(end.diff(start));

  return duration.asHours() <= 24;
};

const AddProjectToDealForm = ({ closeDialog }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();
  const {
    isOpen: isStatusErrorOpen,
    title: statusErrorTitle,
    statusError,
    showStatusError,
    closeStatusError,
  } = useStatusErrorDialog();

  // Did the user make a change??
  const [userModifiedSkipped, setUserModifiedSkipped] = useState(false);

  const statusArr = values(PROJECT_STATUS_STATES);

  const [isSubmittingAddProjectMethodology, setIsSubmittingAddProjectMethodology] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddMethodologyEnabled, setIsAddMethodologyEnabled] = useState(false);

  //Get the Project Methodologies from the Store
  const getAllProjectMethodologies = selectProjectMethodologies();
  const { projectMethodologies } = useSelector(state => getAllProjectMethodologies(state));

  //Get the Deals
  const getAllDealsDropdown = selectDealsDropdown();
  const { deals } = useSelector(state => getAllDealsDropdown(state));

  const { projectToUpdateId, dealToUpdateId } = useStaffingSelector();
  const getProject = selectProject();
  const isUpdate = Boolean(projectToUpdateId);

  //Get the Project Info from the Store
  const project = useSelector(state => getProject(state, projectToUpdateId));

  const getConfig = selectConfigSelectOptions();
  const { selectOptions: vcEventTopics } = useSelector(state => getConfig(state, 'vcEventTopics'));
  const { selectOptions: vcEventRegions } = useSelector(state =>
    getConfig(state, 'vcEventRegions'),
  );
  const { selectOptions: vcEventTypes } = useSelector(state => getConfig(state, 'vcEventTypes'));
  const { selectOptions: vcEventVenues } = useSelector(state => getConfig(state, 'vcEventVenues'));

  const getTimezonesConfig = selectConfigSelectOptions(true);
  const { selectOptions: vcEventTimezones } = useSelector(state =>
    getTimezonesConfig(state, 'vcEventTimezones'),
  );

  const getLanguagesConfig = selectConfigSelectOptions(true);
  const { selectOptions: vcEventLanguages } = useSelector(state =>
    getLanguagesConfig(state, 'vcEventLanguages'),
  );

  //Get the Deal Info from the Store
  const getDeal = selectDeal();
  const deal = useSelector(state => getDeal(state, dealToUpdateId));

  const isPublic = deal?.isPublic;

  const defaultCalendarEventRecurrenceType = PROJECT_CALENDAR_EVENT_RECURRENCE_SELECT_OPTIONS.find(
    option => option.value === 'DAILY',
  ).value;

  //Form Initial Values
  const formInitialValues = {
    calendarEventRecurrenceType:
      project?.calendarEventRecurrenceType ?? defaultCalendarEventRecurrenceType,
    contactName: project?.contactName ?? '',
    contactEmail: project?.contactEmail ?? '',
    contractValue: project?.contractValue ?? deal?.amountLeft ?? 0,
    endDate: project?.endDate ?? '',
    endDateLocal: project?.endDateLocal ?? '',
    endTime: project?.endTime ?? '',
    endTimeLocal: project?.endTimeLocal ?? '15:00:00',
    eventName: project?.eventName ?? '',
    eventSubtitle: project?.eventSubtitle ?? '',
    eventRegion: project?.eventRegion ?? '',
    eventTopic: project?.eventTopic ?? '',
    eventType: project?.eventType ?? '',
    isFree: project?.isFree ?? false,
    locationType: project?.locationType ?? '',
    notes: project?.notes ?? '',
    certProjectBoardsProvider:
      project?.certProjectBoardsProvider ?? CERT_PROJECT_BOARDS_PROVIDER.Miro,
    projectMethodologyId: project?.projectMethodologyId ?? '',
    serviceType: project?.serviceType ?? '',
    startDate: project?.startDate ?? '',
    startDateLocal: project?.startDateLocal ?? '',
    startTime: project?.startTime ?? '',
    startTimeLocal: project?.startTimeLocal ?? '10:00:00',
    venue: project?.venue ?? '',
    venueId: project?.venueId ?? '',
    // Backend defaults missing provider to Zoom; infer Meet when only meetingLink exists (older docs).
    meetingProvider:
      project?.meetingProvider ??
      (project?.meetingLink && !project?.zoomLink
        ? MEETING_PROVIDERS.googleMeet
        : MEETING_PROVIDERS.zoom),
    meetingLink: project?.meetingLink ?? '',
    zoomUserEmail: project?.zoomUserEmail ?? '',
    timezone: project?.timezone ?? '',
    language: project?.language ?? 'en',
    skippedMeetingOccurrences: project?.skippedMeetingOccurrences?.join(', ') ?? '',
    eventFeaturedContent: project?.eventFeaturedContent ?? '',
  };

  // get the project status with '-error' stripped off if it is there
  const nonErrorStatus = project?.status
    ? project.status.endsWith('-error')
      ? trimEnd(project.status, '-error')
      : project.status
    : null;

  // const isInProcess = () =>
  //   statusArr.indexOf(nonErrorStatus) > statusArr.indexOf(PROJECT_STATUS_STATES.SCHEDULED);

  const isScheduled = () =>
    statusArr.indexOf(nonErrorStatus) > statusArr.indexOf(PROJECT_STATUS_STATES.ADDING_CALENDAR);
  const dateRequiredWhenScheduled = value =>
    !isUpdate || !isScheduled() || (value && isScheduled());
  const isValidTime = value => !value || (value && moment(value, 'HH:mm:ss').isValid());
  // Define the single validation function outside
  const isValidEndTime = (value, testContext) => {
    // Check if required when scheduled
    if (!dateRequiredWhenScheduled(value)) {
      return testContext.createError({ message: 'Required once the project has been scheduled' });
    }

    // Check if it's a valid time
    if (!isValidTime(value)) {
      return testContext.createError({ message: 'Must be a valid time' });
    }

    return true;
  };

  const isWpEventTaxFieldValid = (value, serviceType) => {
    const isCert = serviceType === PROJECT_SERVICES.Certification;
    if (isCert) return true;
    if (!isPublic) return true;
    return Boolean(value);
  };

  const timezoneRequiredWhenDateIsComplete = (value, data) => {
    const dateComplete =
      data.startDateLocal && data.endDateLocal && data.startTimeLocal && data.endTimeLocal;
    return (dateComplete && value) || !dateComplete;
  };

  // Yup schema
  const formSchema = Yup.object().shape({
    locationType: Yup.string().required('Required'),
    contactName: Yup.string(),
    contactEmail: Yup.string().email('Contact Email should be a valid email'),
    zoomUserEmail: Yup.string().when(['locationType', 'meetingProvider'], {
      is: (locationType, meetingProvider) =>
        locationType !== 'in_person' && meetingProvider === MEETING_PROVIDERS.zoom,
      then: Yup.string().required('Required when using Zoom for Virtual or Hybrid projects'),
      otherwise: Yup.string().notRequired(),
    }),
    meetingProvider: Yup.string().when('locationType', {
      is: locationType =>
        locationType === PROJECT_LOCATION_TYPES.hybrid ||
        locationType === PROJECT_LOCATION_TYPES.virtual,
      then: Yup.string()
        .oneOf([MEETING_PROVIDERS.zoom, MEETING_PROVIDERS.googleMeet], 'Invalid meeting platform')
        .required('Required'),
      otherwise: Yup.string().notRequired(),
    }),
    // Google Meet join URLs are generated by Control Room on schedule; the form field is read-only
    // after create and is never submitted (see handleAddProject).
    meetingLink: Yup.string().notRequired(),
    venueId: Yup.string().when('locationType', {
      is: locationType => locationType !== 'virtual',
      then: Yup.string().required('Required when the location type is In-Person or Hybrid'),
      otherwise: Yup.string().notRequired(),
    }),
    eventName: Yup.string().required('Required'),
    eventSubtitle: Yup.string(),
    eventRegion: Yup.string().test(
      'required-when-public',
      'Required for public projects',
      function (value) {
        const serviceType = this.parent.serviceType;
        return isWpEventTaxFieldValid(value, serviceType);
      },
    ),
    eventTopic: Yup.string().test(
      'required-when-public',
      'Required for public projects',
      function (value) {
        const serviceType = this.parent.serviceType;
        return isWpEventTaxFieldValid(value, serviceType);
      },
    ),
    eventType: Yup.string().test(
      'required-when-public',
      'Required for public projects',
      function (value) {
        const serviceType = this.parent.serviceType;
        return isWpEventTaxFieldValid(value, serviceType);
      },
    ),
    serviceType: Yup.string()
      .required('Required')
      .test(
        'service-type-validation',
        'Public projects must be either Training, Certification, or Facilitation (for Practice Playground only)',
        function (value) {
          const methodologyId = this.parent.projectMethodologyId;
          const methodology = projectMethodologies.find(m => m.value === methodologyId)?.name;
          return (
            projectToUpdateId ||
            !isPublic ||
            ['Training', 'Certification'].includes(value) ||
            (value === 'Facilitation' && methodology === 'Practice Playground')
          );
        },
      ),
    certProjectBoardsProvider: Yup.string().test(
      'required-when-cert',
      'Required to create a certification project',
      function (value) {
        return (
          (this.parent.serviceType === PROJECT_SERVICES.Certification && value) ||
          this.parent.serviceType !== PROJECT_SERVICES.Certification
        );
      },
    ),
    projectMethodologyId: Yup.string().required('Required'),
    startDateLocal: Yup.date()
      .test(
        'required-when-public',
        'Required to create a public project',
        value => (isPublic && value) || !isPublic,
      )
      .test('required-when-scheduled', 'Required once the project has been scheduled', value =>
        dateRequiredWhenScheduled(value),
      ),
    startTimeLocal: Yup.string()
      .test(
        'required-when-public',
        'Required to create a public project',
        value => (isPublic && value) || !isPublic,
      )
      .test('required-when-scheduled', 'Required once the project has been scheduled', value =>
        dateRequiredWhenScheduled(value),
      )
      .test('is-time', 'must be a valid time', value => isValidTime(value))
      .nullable(),
    endDateLocal: Yup.date()
      .min(Yup.ref('startDateLocal'), "End Date can't be set before the Start Date")
      .test('required-when-scheduled', 'Required once the project has been scheduled', value =>
        dateRequiredWhenScheduled(value),
      ),
    endTimeLocal: Yup.string().test('end-time-validation', isValidEndTime),
    timezone: Yup.string()
      .test('required-when-scheduled', 'Required once the project has been scheduled', value =>
        dateRequiredWhenScheduled(value),
      )
      .test(
        'required-when-date-is-complete',
        'Required when the date is complete',
        function (value) {
          return timezoneRequiredWhenDateIsComplete(value, this.parent);
        },
      ),
    isFree: Yup.boolean(),
    contractValue: Yup.number()
      .max(
        (deal?.amountLeft || 0) + formInitialValues.contractValue,
        "Must be less than the Deal's remaining estimated value",
      )
      .min(0, 'Must be greater than or equal to 0'),
    notes: Yup.string(),
    eventFeaturedContent: Yup.string().test(
      'required-when-event-type-meetup',
      'Required when the event type is Meetup',
      function (value) {
        const eventType = this.parent.eventType;
        return (eventType === 'meetup' && value) || eventType !== 'meetup';
      },
    ),
    calendarEventRecurrenceType: Yup.string().required('Required'),
    skippedMeetingOccurrences: Yup.string().matches(/^(\d+(\s*,\s*\d+)*)?$/, {
      message: 'Must be a comma-separated list of numbers (e.g., 1, 2, 3)',
      excludeEmptyString: true,
    }),
  });

  const handleFormSubmit = async (values, { setErrors }) => {
    const fieldsToCheck = [
      'locationType',
      'startDateLocal',
      'endDateLocal',
      'startTimeLocal',
      'endTimeLocal',
      'timezone',
      'language',
      'startDate',
      'endDate',
      'startTime',
      'endTime',
      'calendarEventRecurrenceType',
      'venue',
      'venueId',
      'meetingProvider',
      'meetingLink',
      'zoomUserEmail',
      'eventType',
      'eventTopic',
      'eventRegion',
      'eventSubtitle',
      'eventName',
      'notes',
      'skippedMeetingOccurrences',
    ];

    const remindUserToFixProjectFolderName =
      isPublic && projectToUpdateId && values.startDate !== formInitialValues.startDate;

    const changedScheduleFields =
      isScheduled() && projectToUpdateId
        ? fieldsToCheck.filter(field => values[field] !== formInitialValues[field])
        : [];
    const remindUserToRunResyncCalendar = changedScheduleFields.length > 0;

    if (remindUserToFixProjectFolderName || remindUserToRunResyncCalendar) {
      let message = '';
      if (remindUserToFixProjectFolderName) {
        message += "Start date is changed.\nYou'll need to manually rename the project folder.";
      }
      if (remindUserToRunResyncCalendar) {
        if (message) {
          message += '\n\n';
        }
        message += `${toSentence(changedScheduleFields)} ${changedScheduleFields.length > 1 ? 'have' : 'has'} changed.\nYou'll need to run Resync Calendar.`;
      }
      confirm({
        description: message,
      }).then(async () => {
        await handleAddProject(values, { setErrors }, remindUserToRunResyncCalendar);
      });
    } else {
      await handleAddProject(values, { setErrors }, remindUserToRunResyncCalendar);
    }
  };

  const handleAddProject = async (values, { setErrors }, remindUserToRunResyncCalendar) => {
    setIsSubmitting(true);

    // Create-only guard: block attaching a new project while the deal is still initializing
    // (or failed init). Covers races where the dialog opened before status flipped, or a
    // pre-set dealToUpdateId that is no longer eligible. Editing an existing project is fine.
    if (!projectToUpdateId && isDealInitializing(deal?.status)) {
      dispatch(enqueueErrorSnackbar(DEAL_INITIALIZING_ADD_PROJECT_MESSAGE));
      setIsSubmitting(false);
      return;
    }

    delete values.projectMethodologyName;

    // Convert skippedMeetingOccurrences from CSV string to array of numbers
    const skippedOccurrencesArray = values.skippedMeetingOccurrences
      ? values.skippedMeetingOccurrences
          .split(',')
          .map(s => parseInt(s.trim(), 10))
          .filter(n => !isNaN(n) && Number.isInteger(n))
      : [];

    const submissionValues = {
      ...values,
      skippedMeetingOccurrences: skippedOccurrencesArray,
    };

    // Control Room generates and owns Google Meet links; never send client-entered meetingLink.
    if (submissionValues.meetingProvider === MEETING_PROVIDERS.googleMeet) {
      delete submissionValues.meetingLink;
    }

    const successCallback = newProjectId => {
      setIsSubmitting(false);
      closeDialog();
      // Navigate to the new project's detail page
      history.push(`/admin/staffing/projects/detail/${newProjectId}`);
    };

    // Check if end time is after start time
    if (values.startDate && values.endDate && values.startTime && values.endTime) {
      const start = moment(`${values.startDate}T${values.startTime}`);
      const end = moment(`${values.endDate}T${values.endTime}`);
      if (start.isSame(end, 'day') && !end.isAfter(start)) {
        setErrors({ endTime: 'End time must be after start time' });
        setIsSubmitting(false);
        return;
      }
    }

    // Check if the event name is unique per deal, with special handling for public events
    const existingEventsWithSameName = map(deal.projects, project => ({
      name: project.eventName,
      startDate: project.startDate,
    })).filter(event => event.name == submissionValues.eventName);

    if (!projectToUpdateId && existingEventsWithSameName.length > 0) {
      if (isPublic) {
        // For public events, check if there's another event on same day
        const hasSameDayEvent = existingEventsWithSameName.some(event =>
          moment(event.startDate).isSame(submissionValues.startDate, 'day'),
        );

        if (hasSameDayEvent) {
          setErrors({ eventName: 'Event Name for public events must be unique per deal per day' });
          setIsSubmitting(false);
          return;
        }
      } else {
        // For non-public events, any name match is a duplicate
        setErrors({ eventName: 'Event Name must be unique per deal' });
        setIsSubmitting(false);
        return;
      }
    }

    if (values.contactName && !values.contactEmail) {
      setErrors({ contactEmail: 'Contact Email is required when setting Contact Name' });
      setIsSubmitting(false);
      return;
    }

    if (values.contactEmail && !values.contactName) {
      setErrors({ contactName: 'Contact Name is required when setting Contact Name' });
      setIsSubmitting(false);
      return;
    }

    // The API runs its own copy of these rules and can reject a payload the checks above allowed
    // (it sees deal/project context the form does not). Map field errors onto inputs when possible;
    // unmapped fields and generic API messages go to StatusErrorDialog so the reason is always readable.
    try {
      if (projectToUpdateId) {
        //if the DealId has been changed, pass the previousDealId to the updateProject thunk
        const previousDealId = project.dealId !== dealToUpdateId ? project.dealId : '';

        await dispatch(
          updateProject(
            projectToUpdateId,
            {
              ...submissionValues,
              dealId: dealToUpdateId,
              //  We should only include remindUserToRunResyncCalendar if it's true,
              // It should only be changed from true to false when calling resyncCal.
              ...(remindUserToRunResyncCalendar && { remindUserToRunResyncCalendar }),
            },
            successCallback,
            previousDealId,
          ),
        );
      } else {
        await dispatch(
          createProject({ ...submissionValues, dealId: deal.id }, true, successCallback),
        );
      }
    } catch (err) {
      const fieldErrors = getApiFieldErrors(err);

      if (fieldErrors) {
        // Split API field errors into ones we can pin on inputs vs ones with no rendered field.
        const mappedErrors = {};
        const unmappedMessages = [];

        Object.entries(fieldErrors).forEach(([field, message]) => {
          if (PROJECT_FORM_FIELD_NAMES.has(field)) {
            mappedErrors[field] = message;
          } else {
            unmappedMessages.push(message);
          }
        });

        if (Object.keys(mappedErrors).length > 0) {
          setErrors(mappedErrors);
        }

        if (unmappedMessages.length > 0) {
          showStatusError('Error saving project', unmappedMessages.join('\n'));
        }
      } else {
        // No field list — still show the API's message (or network fallback) in the error dialog.
        showStatusError('Error saving project', getApiErrorMessage(err));
      }

      setIsSubmitting(false);
    }
  };

  const handleAddNewMethodologyClick = (methodologyName, setFieldValue) => {
    if (isAddMethodologyEnabled && methodologyName && methodologyName.length > 3) {
      //check if the Methodology name already exists
      if (some(projectMethodologies, { name: methodologyName })) {
        dispatch(
          enqueueErrorSnackbar('Methodology already exists. Please enter another Methodology name'),
        );
        return;
      }

      setIsSubmittingAddProjectMethodology(true);

      // Add Methodology to List. Reset the spinner on success or 409 so a duplicate
      // name does not leave the Done button spinning.
      dispatch(
        createProjectMethodology(methodologyName, methodologyId => {
          setIsAddMethodologyEnabled(false);
          setFieldValue('projectMethodologyId', methodologyId);
          setFieldValue('projectMethodologyName', '');
        }),
      ).finally(() => {
        setIsSubmittingAddProjectMethodology(false);
      });
      return;
    }

    setIsAddMethodologyEnabled(true);
  };

  const toSentence = arr => {
    if (arr.length <= 1) return arr.join('');
    if (arr.length === 2) return arr.join(' and ');
    return arr.slice(0, -1).join(', ') + ', and ' + arr.slice(-1);
  };

  return (
    <>
      <Formik
        validationSchema={formSchema}
        initialValues={formInitialValues}
        onSubmit={handleFormSubmit}
      >
        {({ submitForm, resetForm, values, setFieldValue, errors, touched, submitCount }) => (
          <Form style={{ paddingBottom: 20 }}>
            {console.log('errors', errors)}
            {/* Pass the state to FormListener */}
            <FormListener
              values={values}
              setFieldValue={setFieldValue}
              userModifiedSkipped={userModifiedSkipped}
              setUserModifiedSkipped={setUserModifiedSkipped}
            />
            <Grid container spacing={5}>
              <Grid item xs={6}>
                <TextInput
                  name="eventName"
                  label="Project Name"
                  className=""
                  // Project Name is used in initialize, schedule create assets, so don't allow editing
                  disabled={isUpdate}
                  required
                  additionalStyles={{ label: classes.inputLabel }}
                />
              </Grid>

              <Grid item xs={6}>
                <CustomSearchableSelect
                  label="Deal"
                  name="dealId"
                  customOnChange={(_, value) => {
                    dispatch(
                      setDealsFilterPayload({
                        dealToUpdateId: value.value,
                      }),
                    );
                  }}
                  placeholder=""
                  required
                  valueOverride={
                    deal
                      ? {
                          name: `${deal?.dealName} (${deal?.companyName})`,
                          value: deal?.id,
                        }
                      : null
                  }
                  disableClearable
                  disabled={Boolean(projectToUpdateId)}
                  options={deals}
                  customStyles={{
                    container: classes.selectContainer,
                    label: classes.inputLabel,
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={5}>
              <Grid item xs={6}>
                <TextInput
                  name="contactName"
                  type="string"
                  label="Contact Name"
                  helperText="Deal Contact Name is used when empty"
                  additionalStyles={{
                    container: classes.selectContainer,
                    label: classes.inputLabel,
                  }}
                />
              </Grid>

              <Grid item xs={5}>
                <Box className={classes.methodologyLabelRow}>
                  <InputLabel
                    variant="standard"
                    disableAnimation
                    shrink={false}
                    required
                    htmlFor={
                      isAddMethodologyEnabled ? 'projectMethodologyName' : 'projectMethodologyId'
                    }
                    className={classes.inputLabel}
                    style={{ lineHeight: '1.25' }}
                  >
                    Methodology
                  </InputLabel>
                  <button
                    type="button"
                    className={classes.manageMethodologiesLink}
                    onClick={() => dispatch(openManageProjectMethodologiesDialog(true))}
                    aria-label="Manage methodologies"
                  >
                    <EditIcon />
                  </button>
                </Box>
                {isAddMethodologyEnabled ? (
                  <TextInput
                    name="projectMethodologyName"
                    type="string"
                    required
                    additionalStyles={{
                      label: classes.inputLabel,
                    }}
                    onClear={() => {
                      setIsAddMethodologyEnabled(false);
                      setFieldValue('projectMethodologyName', '');
                    }}
                  />
                ) : (
                  <CustomSelect
                    label=""
                    name="projectMethodologyId"
                    type="select"
                    placeholder=""
                    required
                    selectOptions={projectMethodologies}
                    customStyles={{
                      container: classes.selectContainer,
                      label: classes.inputLabel,
                    }}
                    disabled={isUpdate}
                  />
                )}
              </Grid>

              <Grid item xs={1} container className="pl-0 justify-content-end">
                <Button
                  size="small"
                  variant="text"
                  disabled={isSubmittingAddProjectMethodology || isUpdate}
                  className={cx({
                    // 'mt-4': !isAddMethodologyEnabled,
                    // 'mt-5': isAddMethodologyEnabled,
                    [classes.addMethodologyButtonGrey]:
                      isAddMethodologyEnabled && (values.projectMethodologyName || '').length < 3,
                    [classes.addMethodologyButton]:
                      (values.projectMethodologyName || '').length >= 3 || !isAddMethodologyEnabled,
                  })}
                  onClick={() => {
                    handleAddNewMethodologyClick(values.projectMethodologyName, setFieldValue);
                  }}
                >
                  {isAddMethodologyEnabled ? (
                    isSubmittingAddProjectMethodology ? (
                      <Spinner size={24} />
                    ) : (
                      <DoneIcon />
                    )
                  ) : (
                    <AddIcon />
                  )}
                </Button>
              </Grid>
            </Grid>

            <Grid container spacing={5}>
              <Grid item xs={6}>
                <TextInput
                  name="contactEmail"
                  type="string"
                  label="Contact Email"
                  helperText="Deal Contact Email is used when empty"
                  additionalStyles={{
                    container: classes.selectContainer,
                    label: classes.inputLabel,
                  }}
                />
              </Grid>
              <Grid item xs={6} className="pr-1">
                <CustomSelect
                  label="Service Type"
                  name="serviceType"
                  type="select"
                  placeholder=""
                  required
                  disabled={isUpdate}
                  selectOptions={PROJECT_SERVICE_SELECT_OPTIONS}
                  /**
                   * Skill Bridge is always free; set isFree synchronously so submit
                   * can rely on Formik state even while the Free checkbox is hidden.
                   */
                  afterChange={newServiceType => {
                    syncIsFreeForServiceAndEventType(
                      newServiceType,
                      values.eventType,
                      setFieldValue,
                    );
                  }}
                  customStyles={{
                    container: classes.selectContainer,
                    label: classes.inputLabel,
                  }}
                />
              </Grid>
            </Grid>

            {isPublic && values.serviceType !== 'Certification' && (
              <>
                <Grid container spacing={5}>
                  <Grid item xs={6}>
                    <CustomSearchableSelect
                      label="Event Topic"
                      className="mb-4"
                      name="eventTopic"
                      type="select"
                      placeholder=""
                      required={isPublic}
                      disableClearable
                      options={vcEventTopics}
                      customStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                    />
                  </Grid>

                  <Grid item xs={6} container spacing={2}>
                    <Grid item xs={6}>
                      <CustomSearchableSelect
                        label="Event Type"
                        className="mb-4"
                        name="eventType"
                        type="select"
                        placeholder=""
                        disableClearable
                        required={isPublic}
                        options={vcEventTypes}
                        /**
                         * Keep the Free checkbox in sync when Event Type changes:
                         * always-free types check Free; other types unlock and clear Free
                         * (unless Skill Bridge) so Amount reappears.
                         */
                        afterChange={newEventType => {
                          syncIsFreeForServiceAndEventType(
                            values.serviceType,
                            newEventType,
                            setFieldValue,
                          );
                        }}
                        customStyles={{
                          container: classes.selectContainer,
                          label: classes.inputLabel,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomSearchableSelect
                        label="Event Region"
                        className="mb-4"
                        name="eventRegion"
                        type="select"
                        placeholder=""
                        required={isPublic}
                        disableClearable
                        options={vcEventRegions}
                        customStyles={{
                          container: classes.selectContainer,
                          label: classes.inputLabel,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container spacing={5}>
                  <Grid item xs={6}>
                    <CustomSearchableSelect
                      label="Language"
                      className="mb-4"
                      name="language"
                      type="select"
                      placeholder=""
                      required={isPublic || isScheduled()}
                      disabled={isUpdate}
                      options={vcEventLanguages}
                      customStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <Grid container spacing={5}>
              <Grid item xs={6}>
                <CustomSelect
                  label="Location Type"
                  className="mb-0"
                  name="locationType"
                  type="select"
                  placeholder=""
                  required
                  selectOptions={PROJECT_LOCATION_TYPES_SELECT_OPTIONS}
                  customStyles={{
                    container: classes.selectContainer,
                    label: classes.inputLabel,
                  }}
                />
              </Grid>

              {values.serviceType !== PROJECT_SERVICES.SkillBridge && (
                <Grid item xs={6} container spacing={2}>
                  <Grid item xs={6}>
                    <MaterialDate
                      name="startDateLocal"
                      label="Start Date"
                      allowKeyboardInput={true}
                      className="mb-0"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled() || !formInitialValues.startDate}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MaterialDate
                      name="endDateLocal"
                      label="End Date"
                      allowKeyboardInput={true}
                      className="mb-0"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled() || !formInitialValues.endDate}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MaterialTime
                      name="startTimeLocal"
                      label="Start Time"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled() || !formInitialValues.startTime}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MaterialTime
                      name="endTimeLocal"
                      label="End Time"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled() || !formInitialValues.endTime}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSearchableSelect
                      label="Timezone"
                      className="mb-4" //mb-0 when it was Custom Select
                      name="timezone"
                      type="select"
                      placeholder=""
                      required={isPublic || isScheduled()}
                      disableClearable
                      options={vcEventTimezones}
                      customStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Grid container spacing={5}>
              {[PROJECT_LOCATION_TYPES.hybrid, PROJECT_LOCATION_TYPES.virtual].includes(
                values.locationType,
              ) && (
                <Grid item xs={6}>
                  <CustomSelect
                    label="Meeting Platform"
                    className="mb-0"
                    name="meetingProvider"
                    type="select"
                    placeholder=""
                    required
                    disabled={isUpdate}
                    selectOptions={MEETING_PROVIDER_SELECT_OPTIONS}
                    customStyles={{
                      container: classes.selectContainer,
                      label: classes.inputLabel,
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                {[PROJECT_LOCATION_TYPES.hybrid, PROJECT_LOCATION_TYPES.virtual].includes(
                  values.locationType,
                ) &&
                  values.meetingProvider === MEETING_PROVIDERS.zoom && (
                    <TextInput
                      name="zoomUserEmail"
                      type="string"
                      label="Zoom User Email"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      required={isScheduled()}
                    />
                  )}
                {isUpdate &&
                  [PROJECT_LOCATION_TYPES.hybrid, PROJECT_LOCATION_TYPES.virtual].includes(
                    values.locationType,
                  ) &&
                  values.meetingProvider === MEETING_PROVIDERS.googleMeet && (
                    <TextInput
                      name="meetingLink"
                      type="string"
                      label="Generated Google Meet link"
                      placeholder="Created when the project is scheduled"
                      helperText="Control Room generates this link from Google Calendar when the project is scheduled. This field is read-only. Legacy pasted links may still appear on older projects until they phase out."
                      disabled
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                    />
                  )}
              </Grid>

              {project?.startTime && (
                <Grid item xs={6} container spacing={2}>
                  <Grid item xs={6}>
                    <MaterialDate
                      name="startDate"
                      label="Start Date (Central)"
                      allowKeyboardInput={true}
                      className="mb-0"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled()}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MaterialDate
                      name="endDate"
                      label="End Date (Central)"
                      allowKeyboardInput={true}
                      className="mb-0"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled()}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <MaterialTime
                      name="startTime"
                      label="Start Time (Central)"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled()}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <MaterialTime
                      name="endTime"
                      label="End Time (Central)"
                      additionalStyles={{
                        container: classes.selectContainer,
                        label: classes.inputLabel,
                      }}
                      // required={isPublic || isScheduled()}
                      disabled
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Grid container spacing={5}>
              <Grid item xs={6}>
                {[PROJECT_LOCATION_TYPES.hybrid, PROJECT_LOCATION_TYPES.in_person].includes(
                  values.locationType,
                ) && (
                  <CustomSearchableSelect
                    label="Venue"
                    className="mb-4"
                    name="venueId"
                    type="select"
                    placeholder=""
                    disableClearable
                    options={vcEventVenues}
                    customStyles={{
                      container: classes.selectContainer,
                      label: classes.inputLabel,
                    }}
                  />
                )}
              </Grid>
              {/*
              Skill Bridge stays inherently free (no Free/Amount UI).
              For other services, always show Free: checked+disabled when the
              event type (or Skill Bridge) forces free; otherwise editable.
              Amount only shows when the project is not free.
            */}
              {values.serviceType !== PROJECT_SERVICES.SkillBridge && (
                <Grid item xs={6} container spacing={2}>
                  <Grid item xs={6}>
                    <div>
                      <Typography className={cx(classes.checkboxLabel, 'mb-1')}>Free</Typography>
                      <CustomCheckbox name="isFree" label="" disabled={isForcedFree(values)} />
                    </div>
                  </Grid>
                  {!values.isFree && (
                    <Grid item xs={6}>
                      <TextInput
                        name="contractValue"
                        type="number"
                        label="Amount"
                        className="mb-1"
                        inputProps={{
                          max: deal?.amountLeft ?? 999999,
                          min: 0,
                        }}
                        required
                        additionalStyles={{
                          container: classes.selectContainer,
                          label: classes.inputLabel,
                        }}
                      />
                      {project ? (
                        <Typography className={cx(classes.inputLabel)}>
                          {deal
                            ? `${formatPrice(deal.amountLeft + formInitialValues.contractValue)}`
                            : '$0'}{' '}
                          remaining
                        </Typography>
                      ) : (
                        <Typography className={cx(classes.inputLabel)}>
                          {deal ? `${formatPrice(deal.amountLeft)}` : '$0'} remaining
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>

            {isPublic && (
              <TextInput
                name="eventSubtitle"
                label="Sub Title"
                className=""
                additionalStyles={{ label: classes.inputLabel }}
              />
            )}

            {values.eventType === 'meetup' && (
              <TextArea
                name="eventFeaturedContent"
                type="text"
                label="Meetup Event - Featured Content"
                multiline
                fullWidth
                rows={4}
                className="mb-4"
                additionalStyles={{ label: classes.inputLabel }}
              />
            )}

            <TextArea
              name="notes"
              type="text"
              label="Description"
              multiline
              fullWidth
              rows={4}
              className="mb-4"
              additionalStyles={{ label: classes.inputLabel }}
            />

            <Grid container spacing={5}>
              {values.serviceType === PROJECT_SERVICES.Certification && (
                <Grid item xs={6}>
                  <CustomSelect
                    label="Project Boards Provider"
                    name="certProjectBoardsProvider"
                    type="select"
                    placeholder=""
                    required
                    selectOptions={CERT_PROJECT_BOARDS_PROVIDER_SELECT_OPTIONS}
                    customStyles={{
                      container: classes.selectContainer,
                      label: classes.inputLabel,
                    }}
                    disabled={isUpdate}
                  />
                </Grid>
              )}
              {!isDurationLessThanOrEqual24Hours(
                values.startDateLocal,
                values.startTimeLocal,
                values.endDateLocal,
                values.endTimeLocal,
              ) &&
                values.serviceType !== PROJECT_SERVICES.SkillBridge && (
                  <>
                    <Grid item xs={6}>
                      <CustomSelect
                        label="Calendar Event Recurrence"
                        className="mb-0"
                        name="calendarEventRecurrenceType"
                        type="select"
                        placeholder=""
                        required
                        selectOptions={PROJECT_CALENDAR_EVENT_RECURRENCE_SELECT_OPTIONS}
                        customStyles={{
                          container: classes.selectContainer,
                          label: classes.inputLabel,
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput
                        name="skippedMeetingOccurrences"
                        type="string"
                        label="Skip Occurrences"
                        //helperText="Comma-separated list of numbers (e.g., 1, 2, 3)"
                        additionalStyles={{
                          container: classes.selectContainer,
                          label: classes.inputLabel,
                        }}
                        onChange={() => setUserModifiedSkipped(true)}
                      />
                    </Grid>
                  </>
                )}
            </Grid>

            <div className={classes.dialogActions}>
              <Button
                className={classes.cancelButton}
                disabled={isSubmitting}
                onClick={() => {
                  closeDialog();
                  resetForm();
                }}
              >
                CANCEL
              </Button>
              <div className={classes.saveButtonContainer}>
                <Button
                  className={classes.saveButton}
                  disabled={isSubmitting || !deal}
                  onClick={submitForm}
                >
                  SAVE
                  {isSubmitting && <ButtonSpinner />}
                </Button>
                {submitCount > 0 &&
                  Object.keys(errors).length > 0 &&
                  Object.keys(touched).length > 0 && (
                    <Typography color="error" className={classes.errorMessage}>
                      See errors above.
                    </Typography>
                  )}
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <StatusErrorDialog
        isOpen={isStatusErrorOpen}
        onClose={closeStatusError}
        title={statusErrorTitle}
        statusError={statusError}
      />
    </>
  );
};

const FormListener = ({
  values: {
    skippedMeetingOccurrences,
    serviceType,
    eventType,
    isFree,
    startDateLocal,
    startTimeLocal,
    endDateLocal,
    endTimeLocal,
  },
  setFieldValue,
  userModifiedSkipped,
  setUserModifiedSkipped,
}) => {
  /**
   * When Skill Bridge or an always-free event type is already selected (e.g. edit),
   * ensure the Free checkbox form value is checked so the disabled control shows
   * the correct state without waiting for an Event Type change.
   */
  useEffect(() => {
    const forcedFree =
      serviceType === PROJECT_SERVICES.SkillBridge || isForcedFreeEventType(eventType);
    if (forcedFree && !isFree) {
      setFieldValue('isFree', true);
    }
  }, [serviceType, eventType, isFree, setFieldValue]);

  useEffect(() => {
    const recurring = !isDurationLessThanOrEqual24Hours(
      startDateLocal,
      startTimeLocal,
      endDateLocal,
      endTimeLocal,
    );

    const hasSkip = Boolean(skippedMeetingOccurrences?.trim());
    if (
      serviceType === PROJECT_SERVICES.Certification &&
      recurring &&
      !hasSkip &&
      !userModifiedSkipped
    ) {
      setFieldValue('skippedMeetingOccurrences', '5, 6, 7, 8');
    } else if (!recurring && hasSkip) {
      setFieldValue('skippedMeetingOccurrences', '');
      //setUserModifiedSkipped(false); Let's not reset this
    }
  }, [
    serviceType,
    startDateLocal,
    startTimeLocal,
    endDateLocal,
    endTimeLocal,
    userModifiedSkipped,
    setFieldValue,
    setUserModifiedSkipped,
  ]);

  return null;
};

AddProjectToDealForm.propTypes = {
  closeDialog: PropTypes.func.isRequired,
};

export default AddProjectToDealForm;
